import { CustomLogicBase, CustomLogicEvent, FieldChangedArgs } from '@msdk/common'
import { Entity, EntityContext, ListValue, MeasurementUnitModel, NumericValue, StringValue } from '@msdk/definitions'

export class MeasurementUnitLogic extends CustomLogicBase {
  private currentEntityName = ''
  private allMeasurementUnits: MeasurementUnitModel[] = []
  private allMuConverisions: MuConversionEntity[] = []
  private allEntityNumericFields: EntityNumericFields[] = []

  public async postLoad(entity: EntityContext): Promise<void> {
    this.allMeasurementUnits = (await this.apiClient.read<MeasurementUnitModel[]>(
      'CMPS/api/metadata/measurementunit',
    )) as MeasurementUnitModel[]

    this.allMuConverisions = (await this.apiClient.read<MuConversionEntity[]>(
      'CMPS/api/external/measurementunit',
    )) as MuConversionEntity[]

    this.allEntityNumericFields = (await this.apiClient.read<EntityNumericFields[]>(
      'CMPS/api/composer/basemetadatawithnumerics',
    )) as EntityNumericFields[]

    if (!entity.IsEmpty('METADATA_LIST')) {
      const entityName = (
        ((entity.fields['METADATA_LIST'] as ListValue)?.value as Entity[])[0]?.fields['METADATA'] as StringValue
      ).value

      this.currentEntityName = entityName ? entityName : ''
    }
  }

  public async postSave(_entity: EntityContext, _e: CustomLogicEvent): Promise<void> {
    this.asyncCache.invalidateFrameworkCache('MeasurementUnit')
  }

  public async onFieldChanged(entity: EntityContext, args: FieldChangedArgs): Promise<void> {
    /* when a field is selected, we fill automatically the source MU */
    if (args.fieldName === 'MEASUREMENT_UNIT_FIELD_NAME') {
      if (!entity.IsEmpty('MEASUREMENT_UNIT_FIELD_NAME') && !entity.IsEmpty('METADATA_LIST')) {
        const entityNameValue = (
          ((entity.fields['METADATA_LIST'] as ListValue).value as Entity[])[0].fields['METADATA'] as StringValue
        ).value
        const fieldNameValue = (entity.fields['MEASUREMENT_UNIT_FIELD_NAME'] as StringValue).value

        const numFieldItem: NumericItem | undefined = this.allEntityNumericFields
          .find((x) => x.entity.name == entityNameValue)
          ?.numericFields.find((x) => x.name == fieldNameValue)
        if (numFieldItem && numFieldItem.um) {
          entity.fields['MEASUREMENT_UNIT_SOURCE'] = new StringValue()
          entity.fields['MEASUREMENT_UNIT_SOURCE'].value = numFieldItem.um
        } else entity.fields['MEASUREMENT_UNIT_SOURCE'] = null
      } else {
        entity.fields['MEASUREMENT_UNIT_SOURCE'] = null
      }
    }

    /* when a new entity is selected, we clear field selector and source MU */
    if (args.fieldName === 'METADATA_LIST') {
      if (!entity.IsEmpty('METADATA_LIST') && !entity.IsEmpty('METADATA_LIST')) {
        const entityNameValue = (
          ((entity.fields['METADATA_LIST'] as ListValue).value as Entity[])[0].fields['METADATA'] as StringValue
        ).value
        if (entityNameValue != this.currentEntityName) {
          entity.fields['MEASUREMENT_UNIT_FIELD_NAME'] = null
          entity.fields['MEASUREMENT_UNIT_SOURCE'] = null
          if (entityNameValue) this.currentEntityName = entityNameValue
        }
      } else {
        entity.fields['MEASUREMENT_UNIT_FIELD_NAME'] = null
        entity.fields['MEASUREMENT_UNIT_SOURCE'] = null
      }
    }

    if (args.fieldName === 'MEASUREMENT_UNIT_DESTINATION' || args.fieldName === 'MEASUREMENT_UNIT_SOURCE') {
      this.setConversionFactorOffset(entity)
    }
  }

  /* we check if there is already the same conversion of DB and we set eventually a validation */
  public async validate(entity: EntityContext): Promise<void> {
    let entityNameValue: string | undefined = undefined
    if (!entity.IsEmpty('METADATA_LIST')) {
      entityNameValue = (
        ((entity.fields['METADATA_LIST'] as ListValue)?.value as Entity[])[0].fields['METADATA'] as StringValue
      ).value
    }
    const fieldName = entity.Str('MEASUREMENT_UNIT_FIELD_NAME')
    const muFrom = entity.Str('MEASUREMENT_UNIT_SOURCE')

    const sameConversion = this.allMuConverisions.find(
      (x) =>
        (x.entityName ? x.entityName : '') == (entityNameValue ? entityNameValue : '') &&
        (x.fieldName ? x.fieldName : '') == (fieldName ? fieldName : '') &&
        x.muFrom == muFrom,
    )

    if (sameConversion && sameConversion.id != entity.id) {
      entity.addValidation('MEASUREMENT_UNIT_SOURCE', 'MU_CONVERSION_00001')
    }
  }

  private setConversionFactorOffset(entity: EntityContext) {
    if (!entity.IsEmpty('MEASUREMENT_UNIT_SOURCE') && !entity.IsEmpty('MEASUREMENT_UNIT_DESTINATION')) {
      const muSource = this.allMeasurementUnits.find(
        (x) => x.key == (entity.fields['MEASUREMENT_UNIT_SOURCE'] as StringValue).value,
      )
      const muDestination = this.allMeasurementUnits.find(
        (x) => x.key == (entity.fields['MEASUREMENT_UNIT_DESTINATION'] as StringValue).value,
      )
      if (
        !!muSource &&
        !!muDestination &&
        (muSource.key == muDestination.key ||
          (!!muSource.conversionFamily &&
            !!muDestination.conversionFamily &&
            muSource.conversionFamily === muDestination.conversionFamily))
      ) {
        const convFactorSource = muSource.conversionFactor == 0 ? 1 : muSource.conversionFactor
        const convFactorDest = muDestination.conversionFactor == 0 ? 1 : muDestination.conversionFactor
        const convOffsetSource = muSource.conversionOffset
        const convOffsetDest = muDestination.conversionOffset

        const convFactor: number = convFactorSource / convFactorDest
        entity.fields['MEASUREMENT_UNIT_CONVERSION_FACTOR'] = new NumericValue()
        entity.fields['MEASUREMENT_UNIT_CONVERSION_FACTOR'].value = Number(convFactor.toFixed(4))

        const convOffset: number = (convOffsetSource - convOffsetDest) / convFactorDest
        entity.fields['MEASUREMENT_UNIT_CONVERSION_OFFSET'] = new NumericValue()
        entity.fields['MEASUREMENT_UNIT_CONVERSION_OFFSET'].value = Number(convOffset.toFixed(2))
      } else {
        entity.fields['MEASUREMENT_UNIT_CONVERSION_FACTOR'] = null
        entity.fields['MEASUREMENT_UNIT_CONVERSION_OFFSET'] = null
      }
    }
  }
}

export interface MuConversionEntity {
  id: string
  entityName?: string
  fieldName?: string
  muFrom: string
  muTo: string
}

export interface EntityNumericFields {
  entity: ModelItem
  numericFields: ModelItem[]
}

export interface ModelItem {
  name: string
  idml: string
  description: string
  tDescription?: string
}

export interface NumericItem extends ModelItem {
  um?: string
  format?: string
}
